import ListingCardV3 from 'components/listing-card/listing-card-v3';
import ImageLoader from 'components/image-loader';
import LoadingSpinner from 'components/loading-spinner';
import { useOnListingCardClick } from 'utils/listing-card';
import { PROPERTY_FAVOURITED_SOURCE_AREA_PAGE } from '@bedrock/analytics';
import type { ListingCardData } from 'components/listing-card';
import styles from './collections-listing-grid.module.scss';

export type CollectionsListingGridProps = {
  listings: readonly ListingCardData[];
  isLoading: boolean;
  /** Shown in place of the grid once loading finishes with nothing to show. */
  emptyMessage: string;
  /** True when the fetch failed, which is shown instead of the empty message. */
  hasError?: boolean;
  /** Shown in place of the grid when `hasError` is set. */
  errorMessage?: string;
  testId: string;
};

/**
 * Flex grid of area-page listing cards (listing-card-v3, the same visual the
 * area page's map side panel uses), shared by the Collections pane's Favourites
 * and Recently Viewed tabs. It renders exactly the `listings` it is handed;
 * paging (where a tab has it) is applied upstream before the slice arrives here.
 */
export function CollectionsListingGrid({ listings, isLoading, emptyMessage, hasError, errorMessage, testId }: CollectionsListingGridProps) {
  const onListingCardClick = useOnListingCardClick({ isMapSidePanel: true });

  if (isLoading) {
    return (
      <div className={styles.state}>
        <LoadingSpinner />
      </div>
    );
  }

  // A failed fetch is not an empty list, so it says so rather than telling the
  // user they have nothing saved.
  if (hasError) {
    return (
      <div className={styles.state} data-testid={`${testId}-Error`} role="alert">
        <ImageLoader
          className={styles.errorImage}
          src="/next/assets/images/not-found-error.svg"
          alt=""
          width="8em"
          height="8em"
        />
        <p className={styles.empty}>{errorMessage}</p>
      </div>
    );
  }

  if (listings.length === 0) {
    return <p className={styles.empty}>{emptyMessage}</p>;
  }

  return (
    <div className={styles.grid} data-testid={testId}>
      {listings.map((listing, index) => (
        <div key={`${listing.id}-${index}`} className={styles.cell}>
          <ListingCardV3
            listing={listing}
            // Eager-load only the first row's images; the rest are below the fold.
            isImageVisible={index < 4}
            onListingCardClick={onListingCardClick}
            favouriteSource={PROPERTY_FAVOURITED_SOURCE_AREA_PAGE}
          />
        </div>
      ))}
    </div>
  );
}
